import { ApiClientError } from "@gettactic/api/src/endpoints/response/ApiClientError";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/api";
import { normalizeDedicatedUsers } from "@/lib/dedicated-users";

const DEFAULT_RETRY_LIMIT = 3;

/** A 4xx (no access, no such resource) answers the same way every time. */
const retryUnlessClientError = (
  failureCount: number,
  error: unknown
): boolean => {
  const status = error instanceof ApiClientError ? error.response?.status : 0;
  if (status >= 400 && status < 500) return false;
  return failureCount < DEFAULT_RETRY_LIMIT;
};

/**
 * The people a dedicated resource belongs to. Pass `null` while the resource
 * is unknown, or when the viewer may not read owners, and the query waits
 * rather than fetching nothing. Shares its key with the owners section, whose
 * invalidations refresh every reader.
 */
export const useResourceDedicatedUsers = (resourceId: string | null) =>
  useQuery({
    queryKey: ["resources.dedicatedUsers", resourceId],
    queryFn: async () => {
      if (!resourceId) return [];
      const response =
        await api.client.organizations.resourceDedicatedUsersGet(resourceId);
      if (!response.ok) {
        // Surface the failure rather than caching an empty owner list.
        const error = new ApiClientError(
          `Could not load the owners of resource ${resourceId} (HTTP ${response.status})`
        );
        error.response = response;
        throw error;
      }
      return normalizeDedicatedUsers(response.result);
    },
    enabled: resourceId !== null,
    retry: retryUnlessClientError
  });
